import { AlexaVisibilityQuerySet } from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { DropdownOption } from '@Components/Dropdown/dropdown-constants';

export const PAGE_NAME = 'Alexa Visibility';

export const AlexaVisibilityTab = {
  QUERY_OVERVIEW: 'query-overview',
  ATTRIBUTES: 'attributes',
  CITATION_COMPARISON: 'citation-comparison',
  COMPETITIVE_COMPARISON: 'competitive-comparison',
  TRENDS: 'trends',
  ALL_RESULTS: 'all-results',
  QUESTIONS: 'questions'
} as const;
export type AlexaVisibilityTabType = (typeof AlexaVisibilityTab)[keyof typeof AlexaVisibilityTab];

export const NAVS: { label: string; key: AlexaVisibilityTabType }[] = [
  { label: 'Query Overview', key: AlexaVisibilityTab.QUERY_OVERVIEW },
  { label: 'Attributes', key: AlexaVisibilityTab.ATTRIBUTES },
  { label: 'Citation Comparison', key: AlexaVisibilityTab.CITATION_COMPARISON },
  {
    label: 'Competitive Comparison',
    key: AlexaVisibilityTab.COMPETITIVE_COMPARISON
  },
  { label: 'Trends', key: AlexaVisibilityTab.TRENDS },
  { label: 'All Results', key: AlexaVisibilityTab.ALL_RESULTS },
  { label: 'Questions', key: AlexaVisibilityTab.QUESTIONS }
];

export const QUERY_SET_OPTIONS: DropdownOption<AlexaVisibilityQuerySet>[] = [
  { label: 'All Queries', value: 'all' },
  { label: 'Query Set A', value: 'a' },
  { label: 'Query Set B', value: 'b' }
];

// Explains the two numbers on every KPI card; shown above the cards.
export const KPI_NOTE =
  'Large number: all runs in view. Small number: most recent run (within the selected dates).';

// Hover definitions shown next to each Query Overview KPI card label.
export const KPI_DEFINITIONS = {
  VISIBILITY_SCORE: 'Percent of results in which Alexa mentioned the selected brand.',
  SHARE_OF_VOICE:
    'Of all brand mentions across results (the selected brand plus competitors), the percent that were the selected brand.',
  TYPICAL_RANK:
    'The rank at which the selected brand most often appears when Alexa mentions it. If ranks tie, the better (lower) rank is shown. Unranked means the brand was never given a rank.',
  TOTAL_RESULTS: 'Number of Alexa responses in the current view.',
  BRANDS_COMPARED:
    'Number of distinct brands mentioned across all results, including the selected brand.',
  STRONG_CITATIONS:
    "Number of results where Alexa's citation was classified as strong, and the percent of all results that represents."
} as const;

// Short chart labels for the classifier's citation types, in the order the chart shows them.
// Types the classifier adds later still appear, after these, under their own name.
export const CITATION_TYPE_LABELS: Record<string, string> = {
  'Named Brand Citation': 'Named Brand',
  'Attribute/Feature Citation': 'Attribute',
  'Competitive Comparison Citation': 'Competitive',
  'No Citation / Not Recommended': 'No Citation'
};

// How many brands the Query Overview brand-mentions chart shows.
export const TOP_BRANDS_LIMIT = 8;

// Competitor brands cycle through Tinuiti's 20-color chart palette, minus the one green in it
// (index 6) so the chosen brand's green stays unique.
export const BRAND_COLOR_COUNT = 19;
export const BRAND_COLOR_SKIPPED_INDEX = 6;

// How many products the Query Overview top-products chart shows.
export const TOP_PRODUCTS_LIMIT = 15;

// How many attributes each brand's chart on the Attributes tab shows.
export const TOP_ATTRIBUTES_LIMIT = 20;

// Most brands the Attributes tab's attribute comparison chart shows.
export const ATTRIBUTE_COMPARISON_BRANDS_LIMIT = 15;

// How many citation sources the Query Overview top-sources chart shows.
export const TOP_SOURCES_LIMIT = 20;

// Most products listed per source in the sources export's summary column.
export const EXPORT_PRODUCTS_PER_SOURCE = 10;

// The results endpoint returns only the latest run when it gets no run or date filter, so the page
// always sends this floor date to get every run (the server still caps a response at the newest
// 1,000 results). The user's date filter then narrows that set in the browser.
export const ALL_RESULTS_FROM_DATE = '2000-01-01';

// "No date filter": every tab aggregates across all runs.
export const EMPTY_DATE_RANGE: DateRange = { start: '', end: '' };

// Hover explanations shown next to the Query Overview chart titles.
export const CHART_DEFINITIONS = {
  CITATION_TYPES:
    'How Alexa cited the selected brand in each response, across the runs in view. A response can have more than one citation type, so the percentages add up to more than 100%. Each percentage is the share of all results.',
  TOP_BRANDS:
    "The brands Alexa mentioned most often across the results, with the selected brand (in green) always shown. Each percentage is that brand's share of all brand mentions, not just of the brands shown."
} as const;

export const PRODUCT_CHART_DEFINITION =
  "The specific products Alexa recommended most often across the results in view (each response's top 3). Each percentage is that product's share of all product recommendations. The selected brand is green; each other brand has its own color.";

export const SOURCES_CHART_DEFINITION =
  'The publications and sites Alexa cited most often to support its recommendations, across the results in view. The Excel export lists every source and, for each, the products recommended in the responses that cite it.';

// Citation Comparison tab limits.
// Brand products listed in the "brand citation summary" card, products per query card, and so on.
export const CITATION_SUMMARY_PRODUCTS_LIMIT = 5;
export const CITATION_CARD_PRODUCTS_LIMIT = 3;
export const CITATION_ATTRIBUTES_LIMIT = 12;
export const CITATION_MISSING_ATTRIBUTES_LIMIT = 12;
export const TOP_FOLLOWUPS_LIMIT = 10;

// Dropdown value for "everything" in the Citation Comparison filters.
export const FILTER_ALL = '__all__';

// Trends tab: brands drawn at first (the rest can be switched on), and chart heights.
export const DEFAULT_VISIBLE_TREND_BRANDS = 5;
export const TREND_CHART_HEIGHT = 340;
export const PRODUCT_TREND_CHART_HEIGHT = 240;

export const ACTIVE_RUN_STATUSES = new Set(['pending', 'running']);

// How often to poll the run history while a scrape is pending/running.
export const RUN_POLL_INTERVAL_MS = 10_000;
